import React, { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { RefreshCw, Send, Loader2, Play, Lock, Check, AlertCircle, Upload, UploadCloud, Film, Camera, Square, Sparkles, Download, Undo2 } from 'lucide-react';
import VideoWorkbench from './VideoWorkbench';
import { FaceLockButton } from './FaceLock';
import { toast } from '../utils/toast';
const API = `${window.__API_BASE}/api`;

const BASE_URL = window.__API_BASE;
const resolveUrl = (url) => !url ? '' : url.startsWith('http') ? url : `${BASE_URL}${url}`;

export function VideoCard({ shot, onGenerateVideo, onFeedback, onLock, onMotionControl, onStopVideo, aspectClass = 'aspect-video', project, assetBible, isPremium, onReload, onImageClick }) {
  const [showHappyHorse, setShowHappyHorse] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [locking, setLocking] = useState(false);
  const [refImage, setRefImage] = useState(null);
  const [refPreview, setRefPreview] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [showMotionControl, setShowMotionControl] = useState(false);
  const [motionFile, setMotionFile] = useState(null);
  const [motionPrompt, setMotionPrompt] = useState('');
  const [motionOrientation, setMotionOrientation] = useState('video');
  const [submittingMotion, setSubmittingMotion] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordedPreview, setRecordedPreview] = useState(null);
  // i2v prompt: controlled state so the ✨ Enhance button can mutate it.
  // `promptDraft` is the live textarea value; `promptOriginal` holds the
  // pre-enhance value so "Undo" can restore it.
  const [promptDraft, setPromptDraft] = useState(shot.i2v_prompt || shot.description || '');
  const [promptOriginal, setPromptOriginal] = useState(null);
  const [enhancing, setEnhancing] = useState(false);
  const [enhanceError, setEnhanceError] = useState('');

  // Three-field motion editor (Feb 2026) — Action / Camera / Emotion (subtext).
  // These map 1:1 to shot fields consumed by build_motion_prompt() on the
  // backend. When the user hits ⏎, we PATCH the shot with the current
  // drafts THEN trigger generate-video, so build_motion_prompt sees the
  // fresh values. If the shot has a manual `i2v_prompt` override, that
  // still wins (the three fields are appended as INNER STATE / camera cues).
  const [actionDraft, setActionDraft] = useState(shot.action_beat || '');
  const [cameraDraft, setCameraDraft] = useState(shot.camera_movement || '');
  const [emotionDraft, setEmotionDraft] = useState(shot.emotion_beat || '');
  const [savingMotion, setSavingMotion] = useState(false);
  const mediaRecorderRef = useRef(null);
  const videoPreviewRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const fileInputRef = useRef(null);
  const motionFileRef = useRef(null);
  const recognitionRef = useRef(null);
  const videoUploadInputRef = useRef(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  // Operator video upload — replaces the current shot video with a
  // user-supplied clip. Mirrors Stage 5's keyframe upload UX.
  const doUploadVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      toast.error('Please choose a video file (.mp4, .mov, .webm).');
      if (videoUploadInputRef.current) videoUploadInputRef.current.value = '';
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast.error('Video too large (100 MB max).');
      if (videoUploadInputRef.current) videoUploadInputRef.current.value = '';
      return;
    }
    setUploadingVideo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(
        `${API}/shots/${shot.id}/video/upload`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      toast.success(`Uploaded video (${((r.data.size_bytes || file.size) / 1024 / 1024).toFixed(1)} MB)`);
      if (onReload) await onReload();
    } catch (err) {
      console.error('video upload failed', err);
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingVideo(false);
      if (videoUploadInputRef.current) videoUploadInputRef.current.value = '';
    }
  };

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment', width: 1280, height: 720 }, audio: false });
      streamRef.current = stream;
      chunksRef.current = [];
      
      // Show live preview immediately
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
        videoPreviewRef.current.muted = true;
        videoPreviewRef.current.play().catch(() => {});
      }
      
      // Use mp4 if supported, otherwise webm
      const mimeType = MediaRecorder.isTypeSupported('video/mp4') ? 'video/mp4' 
        : MediaRecorder.isTypeSupported('video/webm;codecs=vp8') ? 'video/webm;codecs=vp8'
        : 'video/webm';
      
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const ext = mimeType.includes('mp4') ? 'mp4' : 'webm';
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const file = new File([blob], `recording_${Date.now()}.${ext}`, { type: mimeType });
        setMotionFile(file);
        setRecordedPreview(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        if (videoPreviewRef.current) videoPreviewRef.current.srcObject = null;
      };
      recorder.start(200);
      setRecording(true);
    } catch (e) {
      alert('Camera access denied or not available: ' + e.message);
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  }, [recording]);

  const doGenerate = async () => {
    setGenerating(true);
    await onGenerateVideo(shot.id);
    setGenerating(false);
  };

  // ✨ Enhance the i2v prompt: vision-anchored LLM call that takes the
  // operator's draft + Stage-2 fields + script excerpt + asset bindings +
  // brand context + engine target + the keyframe IMAGE, and returns a
  // tight Seedance/Kling-ready motion prompt. Mirrors the keyframe stage's
  // enhance pattern. Feb 2026: writes into the Action field of the
  // three-field motion editor (falls back to promptDraft for legacy uses).
  const doEnhancePrompt = async () => {
    if (enhancing) return;
    setEnhancing(true);
    setEnhanceError('');
    const before = actionDraft || promptDraft || '';
    try {
      const { data } = await axios.post(
        `${API}/shots/${shot.id}/enhance-motion-prompt`,
        { user_input: before }
      );
      const next = (data?.enhanced_prompt || '').trim();
      if (!next) {
        setEnhanceError('No prompt returned');
      } else {
        setPromptOriginal(before);
        setPromptDraft(next);
        setActionDraft(next);
      }
    } catch (e) {
      setEnhanceError(e?.response?.data?.detail || e.message || 'Enhance failed');
    } finally {
      setEnhancing(false);
    }
  };

  // Per-field ✨ auto-generator (Feb 2026, three-field motion editor).
  // Calls the LLM with the keyframe + script + Stage-2 context and writes
  // back into ONE of the three fields ('action' | 'camera' | 'emotion').
  // Backend always returns all 3 (they must be coherent) but only the
  // requested field is persisted server-side.
  const [genField, setGenField] = useState(null); // 'action' | 'camera' | 'emotion' | null
  const [genError, setGenError] = useState('');
  const regenerateField = async (field) => {
    if (genField) return;
    setGenField(field);
    setGenError('');
    try {
      const { data } = await axios.post(
        `${API}/shots/${shot.id}/generate-motion-fields`,
        { field }
      );
      if (field === 'action' && data?.action_beat) setActionDraft(data.action_beat);
      if (field === 'camera' && data?.camera_movement) setCameraDraft(data.camera_movement);
      if (field === 'emotion' && data?.emotion_beat) setEmotionDraft(data.emotion_beat);
    } catch (e) {
      setGenError(e?.response?.data?.detail || e.message || 'Auto-generate failed');
      toast.error(`✨ ${field}: ${e?.response?.data?.detail || e.message || 'failed'}`);
    } finally {
      setGenField(null);
    }
  };

  // Lazy on-open backfill: if all 3 fields are empty and we have a
  // keyframe, quietly generate all 3 in one call. Runs once per card
  // mount. Silent on failure — user can still manually type or hit ✨.
  const [backfillRan, setBackfillRan] = useState(false);
  React.useEffect(() => {
    const allEmpty = !shot.action_beat && !shot.camera_movement && !shot.emotion_beat;
    if (!backfillRan && allEmpty && shot.keyframe_url && !shot.video_locked) {
      setBackfillRan(true);
      // fire-and-forget; UI updates when the response arrives
      (async () => {
        try {
          const { data } = await axios.post(
            `${API}/shots/${shot.id}/generate-motion-fields`,
            { field: 'all' }
          );
          if (data?.action_beat) setActionDraft(data.action_beat);
          if (data?.camera_movement) setCameraDraft(data.camera_movement);
          if (data?.emotion_beat) setEmotionDraft(data.emotion_beat);
        } catch (_e) {
          // silent — user can trigger manually via per-field ✨ or the bulk button
        }
      })();
    }
  }, [shot.id]);

  const undoEnhance = () => {
    if (promptOriginal === null) return;
    setPromptDraft(promptOriginal);
    setActionDraft(promptOriginal);
    setPromptOriginal(null);
    setEnhanceError('');
  };

  // Persist Action/Camera/Emotion drafts to the shot, then trigger
  // video generation. Called on ⏎ in any of the three fields. If a
  // draft matches the shot's current value we skip the PATCH.
  const saveMotionFieldsAndGenerate = async () => {
    if (savingMotion || generating) return;
    const patch = {};
    if ((actionDraft || '') !== (shot.action_beat || '')) patch.action_beat = actionDraft;
    if ((cameraDraft || '') !== (shot.camera_movement || '')) patch.camera_movement = cameraDraft;
    if ((emotionDraft || '') !== (shot.emotion_beat || '')) patch.emotion_beat = emotionDraft;
    setSavingMotion(true);
    try {
      if (Object.keys(patch).length > 0) {
        await axios.patch(`${API}/shots/${shot.id}`, patch);
      }
      if (onGenerateVideo) await onGenerateVideo(shot.id);
    } catch (e) {
      toast.error('Save/generate failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSavingMotion(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setRefImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setRefPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const clearRefImage = () => { setRefImage(null); setRefPreview(null); if (fileInputRef.current) fileInputRef.current.value = ''; };

  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert('Voice input not supported. Try Chrome or Edge.'); return; }
    if (isListening) { recognitionRef.current?.stop(); setIsListening(false); return; }
    const r = new SR(); r.continuous = true; r.interimResults = true; r.lang = 'en-US';
    recognitionRef.current = r;
    let final = feedback;
    r.onresult = (e) => { let interim = ''; for (let i = e.resultIndex; i < e.results.length; i++) { if (e.results[i].isFinal) final += (final ? ' ' : '') + e.results[i][0].transcript; else interim += e.results[i][0].transcript; } setFeedback(final + (interim ? ' ' + interim : '')); };
    r.onend = () => setIsListening(false);
    r.onerror = () => setIsListening(false);
    r.start(); setIsListening(true);
  };

  const doFeedback = async () => {
    if (!feedback.trim() && !refImage) return;
    if (isListening) { recognitionRef.current?.stop(); setIsListening(false); }
    setSending(true);
    let refBase64 = null;
    if (refImage) { refBase64 = await new Promise((res) => { const r = new FileReader(); r.onload = (ev) => res(ev.target.result.split(',')[1]); r.readAsDataURL(refImage); }); }
    await onFeedback(shot.id, feedback, refBase64);
    setSending(false); setFeedback(''); setRefImage(null); setRefPreview(null); setShowFeedback(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const doLock = async () => {
    setLocking(true);
    await onLock(shot.id);
    setLocking(false);
  };

  const doMotionControl = async () => {
    if (!motionFile) { alert('Please select a reference motion video.'); return; }
    setSubmittingMotion(true);
    try {
      await onMotionControl(shot.id, motionFile, motionPrompt, motionOrientation);
      setShowMotionControl(false);
      setMotionFile(null);
      setMotionPrompt('');
      if (motionFileRef.current) motionFileRef.current.value = '';
    } catch (e) {
      alert('Motion control failed: ' + (e.message || 'Unknown error'));
    }
    setSubmittingMotion(false);
  };

  const doStop = async () => {
    if (!onStopVideo) return;
    setStopping(true);
    try { await onStopVideo(shot.id); } catch (e) { console.error('Stop failed:', e); }
    setStopping(false);
  };

  const [downloading, setDownloading] = useState(false);
  const doDownload = useCallback(async (videoUrl) => {
    if (!videoUrl) return;
    setDownloading(true);
    const filename = `${shot.scene_number || 'SC'}-${shot.shot_number || 'SH'}_${shot.id?.slice(0, 8) || 'shot'}.mp4`;
    try {
      // Try auth-fetch + blob first (works for S3 / our backend / same-origin)
      const resp = await fetch(videoUrl, { credentials: 'omit' });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const a = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (err) {
      // Fallback: open in new tab (user can right-click → save)
      console.warn('Blob download failed, falling back to new tab:', err);
      window.open(videoUrl, '_blank');
    } finally {
      setDownloading(false);
    }
  }, [shot.id, shot.scene_number, shot.shot_number]);

  const [videoError, setVideoError] = useState(0);
  const hasVideo = shot.video_url && shot.video_status === 'completed';
  const isProcessing = shot.video_status === 'processing';
  const isFailed = shot.video_status === 'failed';
  const isLocked = shot.video_locked;

  // Resolve best video URL: cycle through fallbacks on error
  const videoFallbacks = [shot.video_url, shot.video_s3_url, shot.video_seedance_url, shot.video_kling_url].filter(u => u);
  const fallbackIdx = videoError || 0;
  const bestVideoUrl = resolveUrl(videoFallbacks[Math.min(fallbackIdx, videoFallbacks.length - 1)] || shot.video_url);

  return (
    <div className={`bg-ai-bg-secondary rounded-xl overflow-hidden border ${isLocked ? 'border-[#34C759]' : 'border-ai-border'} shadow-sm`} data-testid={`video-card-${shot.id}`}>
      <div className={`${aspectClass} bg-ai-bg flex items-center justify-center relative`}>
        {(generating || sending || locking || submittingMotion) && !isProcessing && (
          <div className="absolute inset-0 bg-black/50 z-10 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-white" />
          </div>
        )}
        
        {hasVideo ? (
          <div className="relative w-full h-full">
            {/* Always show keyframe as background poster */}
            {shot.keyframe_url && (
              <img 
                src={resolveUrl(shot.keyframe_s3_url || shot.keyframe_url)} 
                alt="Keyframe" 
                className="absolute inset-0 w-full h-full object-contain z-0"
              />
            )}
            <video 
              src={bestVideoUrl}
              poster={shot.keyframe_url ? resolveUrl(shot.keyframe_s3_url || shot.keyframe_url) : undefined}
              className="relative z-10 w-full h-full object-contain bg-transparent" 
              controls
              preload="metadata"
              onError={() => {
                // Try fallback chain: S3 → Seedance CDN → Kling CDN
                const fallbacks = [shot.video_s3_url, shot.video_seedance_url, shot.video_kling_url].filter(u => u);
                const tried = videoError || 0;
                if (tried < fallbacks.length) {
                  setVideoError(tried + 1);
                }
              }}
              data-testid={`video-player-${shot.id}`}
            />
            {onImageClick && (
              <button
                onClick={(e) => { e.stopPropagation(); onImageClick(); }}
                className="absolute top-2 right-2 z-20 p-1.5 rounded-lg bg-black/60 text-white/90 hover:bg-black/80 transition opacity-70 hover:opacity-100"
                title="Enlarge"
                data-testid={`video-enlarge-${shot.id}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3h6v6" /><path d="M9 21H3v-6" /><path d="M21 3l-7 7" /><path d="M3 21l7-7" />
                </svg>
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); doDownload(bestVideoUrl); }}
              disabled={downloading}
              className="absolute top-2 left-2 z-20 p-1.5 rounded-lg bg-black/60 text-white/90 hover:bg-black/80 transition opacity-70 hover:opacity-100 disabled:opacity-50"
              title="Download video"
              data-testid={`video-download-${shot.id}`}
            >
              {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            </button>
          </div>
        ) : isProcessing ? (
          <div className="relative w-full h-full flex flex-col items-center justify-center text-white">
            {shot.keyframe_url && (
              <img src={resolveUrl(shot.keyframe_url)} alt="" className="absolute inset-0 w-full h-full object-contain opacity-20" />
            )}
            <div className="relative z-10 flex flex-col items-center">
              <div className="relative">
                <Loader2 className="w-10 h-10 animate-spin text-white/80" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-[9px] font-bold text-white" data-testid={`video-progress-pct-${shot.id}`}>
                    {shot.video_progress != null ? `${shot.video_progress}%` : '0%'}
                  </span>
                </div>
              </div>
              <span className="text-[10px] mt-2 text-white/70">
                {shot.video_current_engine 
                  ? `${shot.video_current_engine} — Attempt ${shot.video_current_attempt || 1}/8`
                  : `Starting${shot.video_current_engine ? ` ${shot.video_current_engine}` : ''}...`}
              </span>
              <button
                onClick={doStop}
                disabled={stopping}
                className="mt-2 px-3 py-1 rounded-full text-[10px] font-semibold bg-[#FF3B30]/80 text-white hover:bg-[#FF3B30] transition-all flex items-center gap-1"
                data-testid={`stop-video-btn-${shot.id}`}
              >
                {stopping ? <Loader2 className="w-3 h-3 animate-spin" /> : <Square className="w-3 h-3" />}
                Stop
              </button>
            </div>
          </div>
        ) : isFailed ? (
          <div className="text-center text-[#FF3B30]">
            <AlertCircle className="w-8 h-8 mx-auto mb-2" />
            <span className="text-xs">{shot.video_error || 'Failed'}</span>
          </div>
        ) : shot.keyframe_url ? (
          <div className="relative w-full h-full">
            <img 
              src={resolveUrl(shot.keyframe_url)} 
              alt="Keyframe" 
              className="w-full h-full object-contain opacity-50"
            />
            <div className="absolute inset-0 flex items-center justify-center gap-2">
              <button 
                onClick={doGenerate}
                disabled={generating}
                className="px-4 py-2 bg-ai-accent text-white rounded-full text-xs font-semibold hover:bg-[#0284C7] transition-all flex items-center gap-2"
                data-testid={`generate-video-btn-${shot.id}`}
              >
                <Play className="w-3.5 h-3.5" /> Generate Video
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center text-white/50">
            <span className="text-xs">No keyframe</span>
          </div>
        )}

        {isLocked && (
          <div className="absolute top-2 right-2 px-2 py-1 bg-[#34C759] text-white rounded-full text-[10px] font-semibold flex items-center gap-1">
            <Lock className="w-3 h-3" /> Locked
          </div>
        )}
        {/* Downstream staleness — keyframe was replaced (Face Lock accept /
            regen) after this clip was rendered. */}
        {shot.video_stale && hasVideo && (
          <div className="absolute bottom-2 left-2 z-20 px-1.5 py-0.5 rounded text-[9px] font-medium bg-amber-500/85 text-amber-50 shadow-md pointer-events-none"
            title={shot.video_stale_reason || 'Keyframe changed after this video was rendered — regenerate to match'}
            data-testid={`video-stale-badge-${shot.id}`}>
            Keyframe changed — regenerate
          </div>
        )}
      </div>

      <div className="p-3">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-xs">{shot.scene_number} — {shot.shot_number}</span>
            {shot.video_engine && (
              <span className={`text-[7px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${
                shot.video_engine === 'vertex' ? 'bg-purple-500/20 text-purple-400' :
                shot.video_engine === 'seedance' ? 'bg-cyan-500/20 text-cyan-400' :
                shot.video_engine === 'veo' ? 'bg-emerald-500/20 text-emerald-400' :
                shot.video_engine === 'kling' ? 'bg-rose-500/20 text-rose-400' :
                shot.video_engine === 'higgsfield' ? 'bg-amber-500/20 text-amber-400' :
                'bg-blue-500/20 text-blue-400'
              }`}
                data-testid={`video-engine-badge-${shot.id}`}>
                {shot.video_engine}
              </span>
            )}
          </div>
          <div className="flex gap-1">
            {/* Face Lock — FaceFusion identity-correction pass on the clip (spec §3.1) */}
            {hasVideo && <FaceLockButton shot={shot} media="video" onReload={onReload} />}
            {/* Operator video upload — replaces the current video with a
                user-supplied clip. Hidden file input + small icon button.
                Mirrors the Stage 5 keyframe upload UX. */}
            <input
              type="file"
              ref={videoUploadInputRef}
              accept="video/mp4,video/quicktime,video/webm,video/x-matroska"
              className="hidden"
              onChange={doUploadVideo}
              data-testid={`video-upload-input-${shot.id}`}
            />
            <button
              onClick={() => videoUploadInputRef.current?.click()}
              disabled={uploadingVideo}
              data-testid={`video-upload-btn-${shot.id}`}
              title="Upload your own video (replaces current)"
              className="p-1 rounded-md hover:bg-white/10 transition-colors disabled:opacity-40"
            >
              {uploadingVideo
                ? <Loader2 className="w-3 h-3 animate-spin text-[#0EA5E9]" />
                : <UploadCloud className="w-3 h-3 text-[#0EA5E9]" />}
            </button>
            {/* Workbench — available for any shot with a keyframe */}
            {shot.keyframe_url && (
              <button 
                onClick={() => setShowHappyHorse(true)} 
                data-testid={`video-workbench-btn-${shot.id}`}
                title="Video Generation Workbench"
                className="p-1 rounded-md hover:bg-purple-500/10 transition-colors text-purple-400"
              >
                <Sparkles className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        <p className="text-[10px] text-white/50 line-clamp-2 cursor-pointer hover:text-white/70 transition-colors"
          onClick={(e) => { e.currentTarget.classList.toggle('line-clamp-2'); }}
          title="Click to expand"
        >{shot.i2v_prompt || shot.description}</p>
        {shot.dialogue && (
          <p className="text-[9px] text-cyan-400/70 mt-0.5 line-clamp-1 cursor-pointer hover:line-clamp-none" data-testid={`video-dialogue-${shot.id}`}
            onClick={(e) => { e.stopPropagation(); e.currentTarget.classList.toggle('line-clamp-1'); }}>
            <span className="font-semibold text-cyan-400">DLG:</span> "{shot.dialogue}"
          </p>
        )}
        {shot.voiceover && (
          <p className="text-[9px] text-amber-400/70 mt-0.5 line-clamp-1 cursor-pointer hover:line-clamp-none" data-testid={`video-voiceover-${shot.id}`}
            onClick={(e) => { e.stopPropagation(); e.currentTarget.classList.toggle('line-clamp-1'); }}>
            <span className="font-semibold text-amber-400">VO:</span> "{shot.voiceover}"
          </p>
        )}
        
        {/* Three-field Motion Editor (Feb 2026) —
         *   Action  → shot.action_beat   (what MOVES: verb + intensity adverb)
         *   Camera  → shot.camera_movement (single cinematic term + speed)
         *   Emotion → shot.emotion_beat  (INNER STATE / subtext for micro-expressions)
         *
         * Auto-populated by LLM on-open when all three are empty; per-field
         * ✨ regenerates only that field. ⏎ in any field PATCHes the shot
         * then regenerates the video. Prompt order sent to Seedance/Kling
         * follows research-backed formula: SUBJECT+ACTION → CAMERA → EMOTION. */}
        {!isLocked && shot.keyframe_url && (
          <div className="relative mt-2 space-y-1.5" data-testid={`video-motion-editor-${shot.id}`}>
            {/* ACTION */}
            <div className="relative">
              <label className="block text-[9px] font-semibold uppercase tracking-wider text-[#0EA5E9]/70 mb-0.5"
                htmlFor={`video-action-${shot.id}`}>
                Action <span className="text-white/30 font-normal normal-case">— verb + intensity adverb (e.g. &quot;lifts head slowly&quot;)</span>
              </label>
              <textarea
                id={`video-action-${shot.id}`}
                value={actionDraft}
                onChange={(e) => setActionDraft(e.target.value)}
                disabled={savingMotion || generating || genField === 'action'}
                className="w-full pl-2 pr-8 py-1.5 rounded-sm text-[10px] font-ai text-white/80 resize-none focus:text-white transition-colors disabled:opacity-60"
                style={{ background: 'rgba(14, 165, 233, 0.06)', border: '1px solid rgba(14, 165, 233, 0.15)' }}
                rows={2}
                placeholder="cinematic verb + adverb — e.g. 'glances slowly toward the door'"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    saveMotionFieldsAndGenerate();
                  }
                }}
                data-testid={`video-action-edit-${shot.id}`}
              />
              <button
                type="button"
                onClick={() => regenerateField('action')}
                disabled={!!genField || savingMotion}
                className="absolute top-5 right-1 p-1 rounded-sm text-[#0EA5E9] hover:bg-[#0EA5E9]/15 transition disabled:opacity-50 disabled:cursor-wait"
                title="Auto-generate Action from keyframe + script + Stage-2 context"
                data-testid={`video-regen-action-${shot.id}`}
              >
                {genField === 'action'
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : <Sparkles className="w-3 h-3" />}
              </button>
            </div>

            {/* CAMERA */}
            <div className="relative">
              <label className="block text-[9px] font-semibold uppercase tracking-wider text-purple-400/70 mb-0.5"
                htmlFor={`video-camera-${shot.id}`}>
                Camera <span className="text-white/30 font-normal normal-case">— one term + speed (e.g. &quot;slow push-in&quot;)</span>
              </label>
              <textarea
                id={`video-camera-${shot.id}`}
                value={cameraDraft}
                onChange={(e) => setCameraDraft(e.target.value)}
                disabled={savingMotion || generating || genField === 'camera'}
                className="w-full pl-2 pr-8 py-1.5 rounded-sm text-[10px] font-ai text-white/80 resize-none focus:text-white transition-colors disabled:opacity-60"
                style={{ background: 'rgba(168, 85, 247, 0.06)', border: '1px solid rgba(168, 85, 247, 0.15)' }}
                rows={1}
                placeholder="e.g. 'slow push-in on eyes' · 'static hold' · 'handheld drift left'"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    saveMotionFieldsAndGenerate();
                  }
                }}
                data-testid={`video-camera-edit-${shot.id}`}
              />
              <button
                type="button"
                onClick={() => regenerateField('camera')}
                disabled={!!genField || savingMotion}
                className="absolute top-5 right-1 p-1 rounded-sm text-purple-400 hover:bg-purple-400/15 transition disabled:opacity-50 disabled:cursor-wait"
                title="Auto-generate Camera from keyframe + intent"
                data-testid={`video-regen-camera-${shot.id}`}
              >
                {genField === 'camera'
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : <Sparkles className="w-3 h-3" />}
              </button>
            </div>

            {/* EMOTION / SUBTEXT */}
            <div className="relative">
              <label className="block text-[9px] font-semibold uppercase tracking-wider text-amber-400/70 mb-0.5"
                htmlFor={`video-emotion-${shot.id}`}>
                Emotion / Subtext <span className="text-white/30 font-normal normal-case">— one feeling + one physical tell</span>
              </label>
              <textarea
                id={`video-emotion-${shot.id}`}
                value={emotionDraft}
                onChange={(e) => setEmotionDraft(e.target.value)}
                disabled={savingMotion || generating || genField === 'emotion'}
                className="w-full pl-2 pr-8 py-1.5 rounded-sm text-[10px] font-ai italic text-white/80 resize-none focus:text-white focus:not-italic transition-colors disabled:opacity-60"
                style={{ background: 'rgba(245, 158, 11, 0.06)', border: '1px solid rgba(245, 158, 11, 0.15)' }}
                rows={2}
                placeholder="e.g. 'quiet dread that the call was a mistake, jaw tight'"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    saveMotionFieldsAndGenerate();
                  }
                }}
                data-testid={`video-emotion-edit-${shot.id}`}
              />
              <button
                type="button"
                onClick={() => regenerateField('emotion')}
                disabled={!!genField || savingMotion}
                className="absolute top-5 right-1 p-1 rounded-sm text-amber-400 hover:bg-amber-400/15 transition disabled:opacity-50 disabled:cursor-wait"
                title="Auto-generate Emotion/Subtext from keyframe + intent"
                data-testid={`video-regen-emotion-${shot.id}`}
              >
                {genField === 'emotion'
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : <Sparkles className="w-3 h-3" />}
              </button>
            </div>

            {/* Regenerate CTA */}
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={saveMotionFieldsAndGenerate}
                disabled={savingMotion || generating}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[9px] font-semibold text-[#0EA5E9] hover:bg-[#0EA5E9]/10 border border-[#0EA5E9]/25 transition disabled:opacity-50 disabled:cursor-wait"
                title="Save Action / Camera / Emotion, then regenerate video"
                data-testid={`video-regenerate-btn-${shot.id}`}
              >
                {savingMotion
                  ? <Loader2 className="w-2.5 h-2.5 animate-spin" />
                  : <RefreshCw className="w-2.5 h-2.5" />}
                Save & Regenerate
              </button>
            </div>
            {genError && (
              <div className="text-[9px] text-rose-400/90" data-testid={`video-motion-gen-error-${shot.id}`}>
                {genError}
              </div>
            )}
          </div>
        )}

        {shot.vlm_qa_score && (
          <div className="mt-1 flex items-center gap-2">
            <span className="text-[10px] text-white/50">QA Score:</span>
            <span className={`text-[10px] font-semibold ${shot.vlm_qa_score >= 70 ? 'text-[#34C759]' : 'text-[#FF9500]'}`}>
              {shot.vlm_qa_score}/100
            </span>
          </div>
        )}

        {/* Motion Control Panel */}
      </div>

      {/* Video Generation Workbench Modal */}
      {showHappyHorse && project && (
        <VideoWorkbench
          shotId={shot.id}
          shot={shot}
          project={project}
          assetBible={assetBible}
          isPremium={isPremium}
          onClose={() => setShowHappyHorse(false)}
          onAccept={async () => {
            setShowHappyHorse(false);
            if (onReload) await onReload();
          }}
        />
      )}
    </div>
  );
}
